import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { Box, IconButton } from '@mui/material';
import TechMidForm from '../../../components/projectmng/support/TechMidForm';
import TechCostListForm from '../../../components/projectmng/support/TechCostListForm';
import { listMidTechs } from '../../../modules/projectModule/techMids';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import dayjs from 'dayjs';
import * as yup from 'yup';
import _ from 'lodash';
import * as techApi from '../../../lib/api/tech';

const TechMidEditFormContainer = ({ techMid, changeMode, user }) => {
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();

    const [initialValues, setInitialValues] = useState({
        rptTitle: '',
        rptTyp: '',
        rptStrtDt: '', //작업 시작 일
        rptStrtHr: '00', //작업 시작시간
        rptEndDt: '',
        rptEndHr: '00', //작업
        rptTotTm: 0,
        rptContext: '',
        rptCosts: [{ costTyp: '', costVal: '', costDesc: '' }],
    });

    const validationSchema = yup.object({
        rptTitle: yup.string('').required(' 진행기록명을 입력하세요'),
        rptTyp: yup.string().required(' 진행기록 종류를 선택하세요'),
        rptStrtDt: yup
            .date()
            .required('작업 시작일을 입력하세요')
            .nullable()
            .default(undefined),
        rptEndDt: yup
            .date()
            .required('작업 종료일을 입력하세요')
            .min(yup.ref('rptStrtDt'), '작업 종료일을 확인하세요')
            .when('rptStrtDt', (rptStrtDt, schema) => {
                if (rptStrtDt) return schema.required('작업 종료일을 입력하세요');
            })
            .nullable()
            .default(undefined),
        rptContext: yup.string('').required('진행기록 내용을 입력하세요'),
        rptCosts: yup.array().of(
            yup.object().shape({
                costTyp: yup.string()
                    .nullable()
                    .when('costVal', (costVal, schema) => {
                        if (costVal) return schema.required('비용종류를 선택하세요');
                    }),
                costVal: yup.number('비용은 숫자만 가능합니다').min(0, '비용을 확인하세요')
            })
        ),
    });



    const formik = useFormik({
        initialValues: initialValues,
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitting(true);
            await onSave(values);
            setSubmitting(false);
        },
    });

    useEffect(() => {
        if (techMid) {
            // eslint-disable-next-line react-hooks/exhaustive-deps

            const cloneTechRpt = _.cloneDeep(techMid);
            const strTmArr = cloneTechRpt.rptStrtTm.split(':');
            const endTmArr = cloneTechRpt.rptEndTm.split(':');

            cloneTechRpt.rptStrtHr = strTmArr[0];
            cloneTechRpt.rptEndHr = endTmArr[0];
            cloneTechRpt.rptCosts = techMid?.rptCostList?.length ? techMid.rptCostList : [{ costTyp: '', costVal: '', costDesc: '' }];


            setInitialValues(cloneTechRpt);

        }
    }, [techMid]);

    const getRptCostsToSubmit = (rptCosts = []) => {
        const submittedRptCosts = rptCosts.filter((cost) =>
            cost?.costVal === 0 ||
            (cost?.costVal !== '' && cost?.costVal !== null && typeof cost?.costVal !== 'undefined')
        );

        return submittedRptCosts.length > 0 ? submittedRptCosts : null;
    };

    const makeDataToSubmit = (values) => {
        const rptStrHour = values.rptStrtHr ? values.rptStrtHr + ':00:00' : '00:00:00';
        const rptEndHour = values.rptEndHr ? values.rptEndHr + ':00:00' : '00:00:00';

        const dataToSubmit = {
            rptUuid: techMid.rptUuid,
            reqUuid: techMid.reqUuid,
            prjUuid: techMid.prjUuid,
            planUuid: techMid.planUuid,
            rptTitle: values.rptTitle,
            rptTyp: values.rptTyp,
            rptStrtDt: values.rptStrtDt
                ? dayjs(values.rptStrtDt).format('YYYY-MM-DD')
                : null,
            rptStrtTm: rptStrHour,
            rptEndDt: values.rptEndDt
                ? dayjs(values.rptEndDt).format('YYYY-MM-DD')
                : null,
            rptEndTm: rptEndHour,
            rptContext: values.rptContext,
            rptTotTm: values.rptTotTm,
            rptCostList: getRptCostsToSubmit(values.rptCosts),
        };


        return dataToSubmit;
    };

    const onSave = async (values) => {
        const dataToSubmit = makeDataToSubmit(values);

        try {
            const res = await techApi.editTechMid(techMid.rptUuid, dataToSubmit);

            const isSaved = Boolean(res.data.info);
            if (isSaved) {
                const searchData = { "reqUuid": techMid?.reqUuid, "planUuid": techMid?.planUuid, "prjUuid": techMid?.prjUuid };
                dispatch(listMidTechs(searchData));
                changeMode('view');
            }
            enqueueSnackbar('기술지원 진행기록 저장 성공', {
                variant: 'success',
            });
            return isSaved;
        } catch (err) {
            let message =
                err.response !== 'undefined' ? err.response?.data?.message : err.message;
            enqueueSnackbar(message, {
                variant: 'error',
            });
            return false;
        }
    };




    return (
        <Box
            component="form"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                mt: 1,
            }}
            noValidate
            autoComplete="off"
            onSubmit={formik.handleSubmit}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <IconButton color="default" aria-label="cancle" onClick={() => changeMode('view')}><CancelIcon /></IconButton>
                <IconButton type="submit" color="primary" aria-label="save" disabled={formik.isSubmitting}><SaveIcon /></IconButton>
            </Box>
            <Box>
                <TechMidForm formik={formik} techMid={techMid} />
            </Box>
            <Box>
                <TechCostListForm formik={formik} fieldName="rptCosts" />
            </Box>

        </Box>


    )
}

export default TechMidEditFormContainer
